import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';

const initialState = {
  discounts: [],
  promoCodes: [],
  dealsBanner: null,
  loading: false,
  error: null,
};

// Async Thunk: Fetch Promo Codes
export const fetchPromoCodes = createAsyncThunk(
  'discount/fetchPromoCodes',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/discounts/promocodes');
      if (res.data.success) {
        return res.data.data;
      }
      return rejectWithValue('Failed to fetch promo codes');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { discount } = getState();
      if (discount.promoCodes && discount.promoCodes.length > 0) {
        return false;
      }
    }
  }
);

// Async Thunk: Fetch active automatic discounts (public — storefront cart/checkout)
export const fetchActiveDiscounts = createAsyncThunk(
  'discount/fetchActiveDiscounts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/discounts/active');
      if (res.data.success) {
        return res.data.data;
      }
      return rejectWithValue('Failed to fetch active discounts');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { discount } = getState();
      if (discount.discounts && discount.discounts.length > 0) {
        return false;
      }
    }
  }
);

// Async Thunk: Fetch All Discounts (admin only)
export const fetchAllDiscounts = createAsyncThunk(
  'discount/fetchAllDiscounts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/discounts');
      if (res.data.success) {
        return res.data.data;
      }
      return rejectWithValue('Failed to fetch discounts');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Fetch Deals Banner
export const fetchDealsBanner = createAsyncThunk(
  'discount/fetchDealsBanner',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/discounts/banner');
      if (res.data.success) {
        return res.data.data;
      }
      return rejectWithValue('Failed to fetch deals banner');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { discount } = getState();
      if (discount.dealsBanner) {
        return false;
      }
    }
  }
);

// Async Thunk: Create Discount
export const createDiscount = createAsyncThunk(
  'discount/createDiscount',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/discounts', formData);
      if (res.data.success) {
        return res.data.data;
      }
      return rejectWithValue('Failed to create discount');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Update Discount
export const updateDiscount = createAsyncThunk(
  'discount/updateDiscount',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/discounts/${id}`, formData);
      if (res.data.success) {
        return res.data.data;
      }
      return rejectWithValue('Failed to update discount');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Delete Discount
export const deleteDiscount = createAsyncThunk(
  'discount/deleteDiscount',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/discounts/${id}`);
      if (res.data.success) {
        return id;
      }
      return rejectWithValue('Failed to delete discount');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const discountSlice = createSlice({
  name: 'discount',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Active Discounts (storefront)
      .addCase(fetchActiveDiscounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveDiscounts.fulfilled, (state, action) => {
        state.discounts = action.payload;
        state.loading = false;
      })
      .addCase(fetchActiveDiscounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch All Discounts (admin)
      .addCase(fetchAllDiscounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDiscounts.fulfilled, (state, action) => {
        state.discounts = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllDiscounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Promo Codes
      .addCase(fetchPromoCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodes.fulfilled, (state, action) => {
        state.promoCodes = action.payload;
        state.loading = false;
      })
      .addCase(fetchPromoCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Deals Banner
      .addCase(fetchDealsBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealsBanner.fulfilled, (state, action) => {
        state.dealsBanner = action.payload;
        state.loading = false;
      })
      .addCase(fetchDealsBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Discount
      .addCase(createDiscount.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDiscount.fulfilled, (state, action) => {
        state.discounts.unshift(action.payload);
        state.loading = false;
      })
      .addCase(createDiscount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Discount
      .addCase(updateDiscount.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDiscount.fulfilled, (state, action) => {
        const index = state.discounts.findIndex(d => d._id === action.payload._id);
        if (index !== -1) {
          state.discounts[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateDiscount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Discount
      .addCase(deleteDiscount.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDiscount.fulfilled, (state, action) => {
        state.discounts = state.discounts.filter(d => d._id !== action.payload);
        state.loading = false;
      })
      .addCase(deleteDiscount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export default discountSlice.reducer;
